import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Check, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import './datePicker.css';

/* One date picker and one date-and-time picker, everywhere a teacher picks a
   day or a time (Sandy 2026-10-30 on the browser's own calendar: "it actually
   cant be just like a default one it needs to look good"). House parts only:
   the white panel of the house drop-down, ink for what the teacher reads, one
   gray, the ink filled circle for the picked day, a ring for today, past days
   faded when there is a min. The panel sits on document.body, so a card that
   scrolls never clips it; on phones it is a sheet at the bottom of the screen.
   Time: a box you can type in ("3:30p", "1530", "8:47") with a list of
   15-minute steps beside it. A list alone cannot hold a bell time like
   8:47 AM or a due time of 11:59 PM; a typed box alone is slow for the
   common times. Values stay what the native fields gave: YYYY-MM-DD,
   YYYY-MM-DDTHH:mm and HH:mm, so nothing downstream changes.
   DateField / DateTimeField: a form field that opens the panel.
   DatePopover: the panel alone, for a pill that already shows the date.
   TimeField: the typed time with its list, for a form that keeps the day and
   the time apart (assessments' class time). */

const pad = n => String(n).padStart(2, '0');
const LOCALE = 'en-US';
const WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const dayIso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDay = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
const shift = (d, days) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
const shiftMonth = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, Math.min(d.getDate(), new Date(d.getFullYear(), d.getMonth() + n + 1, 0).getDate()));
const monthStart = d => new Date(d.getFullYear(), d.getMonth(), 1);
const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const startOfToday = () => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); };
const toMin = hhmm => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + m; };

export const timeLabel = hhmm => { const [h, m] = String(hhmm).split(':').map(Number); return `${h % 12 || 12}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`; };
// "Fri, Nov 6"; the year only when it is not this year.
export const dayLabel = iso => {
  const d = toDay(iso);
  return d ? d.toLocaleDateString(LOCALE, { weekday: 'short', month: 'short', day: 'numeric', ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }) }) : '';
};
export const valueLabel = (value, mode = 'date') => !value ? '' : mode === 'date' ? dayLabel(value) : `${dayLabel(value)}, ${timeLabel(value.slice(11, 16) || '00:00')}`;
const fullDay = d => d.toLocaleDateString(LOCALE, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

// A typed time to HH:mm, or null. "3:30 pm", "3:30p", "330p", "15:30", "1530",
// "noon". With no AM/PM the school day decides: 7 to 11 is AM, 12 to 6 is PM;
// an hour written with a 0 first ("07:30", "01:00") is a 24-hour time.
export function parseTime(text) {
  const s = String(text || '').trim().toLowerCase().replace(/[\s.]/g, '');
  if (!s) return null;
  if (s === 'noon') return '12:00';
  if (s === 'midnight') return '00:00';
  const m = /^(\d{1,2})(?::?(\d{2}))?(a|am|p|pm)?$/.exec(s);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  if (min > 59) return null;
  if (m[3]) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (m[3][0] === 'p' ? 12 : 0);
  } else if (h > 23) return null;
  else if (h >= 1 && h <= 6 && m[1][0] !== '0') h += 12;
  return `${pad(h)}:${pad(min)}`;
}

// Phones get the sheet; the same width as the other pickers' phone rules.
const PHONE = '(max-width: 600px)';
function usePhone() {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return undefined;
    const update = () => setPhone(mq.matches);
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return phone;
}

// Keeps an option in its list's view without scrolling the page.
export function keepInView(list, el, center = false) {
  if (!list || !el) return;
  const top = el.offsetTop, bottom = top + el.offsetHeight;
  if (center) list.scrollTop = top - (list.clientHeight - el.offsetHeight) / 2;
  else if (top < list.scrollTop) list.scrollTop = top;
  else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
}

// The box that scrolls an element: its nearest scrolling parent, else the page.
const scroller = el => {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(n).overflowY) && n.scrollHeight > n.clientHeight) return n;
  }
  return document.scrollingElement || document.documentElement;
};

/* The panel: on document.body, under its anchor (above it when there is no
   room below), never off the screen. A click outside closes it ('outside');
   Escape closes it ('escape') and goes no further, so a window behind it
   (the unit report pop-up) stays open. sheet: a sheet at the bottom on
   phones. trap: Tab stays inside (the date panels). */
export function Panel({ anchor, onClose, label, role = 'dialog', sheet = false, trap = false, className = '', head = null, children }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const phone = usePhone();
  const asSheet = sheet && phone;
  const [pos, setPos] = useState(null);
  const fitted = useRef(false);
  useLayoutEffect(() => {
    if (asSheet) return undefined;
    const place = () => {
      const a = anchor.current, p = ref.current;
      if (!a || !p) return;
      let r = a.getBoundingClientRect();
      const w = p.offsetWidth, h = p.offsetHeight, vw = window.innerWidth, vh = window.innerHeight;
      // no room above or below (a short screen, 1366x650): the page scrolls once,
      // just enough, so the panel opens under its field instead of over it
      if (!fitted.current && h) {
        fitted.current = true;
        const below = vh - 8 - (r.bottom + 6);
        if (h > below && h > r.top - 14) {
          const need = Math.min(h - below, r.top - 8);
          if (need > 0) { scroller(a).scrollTop += need; r = a.getBoundingClientRect(); }
        }
      }
      let top = r.bottom + 6;
      if (top + h > vh - 8 && r.top - 6 - h >= 8) top = r.top - 6 - h;
      top = Math.max(8, Math.min(top, vh - h - 8));
      const left = Math.max(8, Math.min(r.left, vw - w - 8));
      setPos(p0 => (p0 && p0.top === top && p0.left === left && p0.min === r.width ? p0 : { top, left, min: r.width }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    // the panel grew (a message under the time): fit it again
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => window.requestAnimationFrame(() => { fitted.current = false; place(); }));
    ro?.observe(ref.current);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); ro?.disconnect(); };
  }, [anchor, asSheet]);
  useEffect(() => {
    const away = e => { if (!ref.current?.contains(e.target) && !anchor.current?.contains(e.target)) closeRef.current('outside'); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [anchor]);
  const onKeyDown = e => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeRef.current('escape'); return; }
    if (e.key !== 'Tab' || !trap) return;
    const items = [...ref.current.querySelectorAll('button:not(:disabled):not([tabindex="-1"]), input:not(:disabled)')];
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  // unplaced for the one render before its size is known: see-through, not
  // hidden, so the day inside can already take the cursor
  const style = asSheet ? undefined : { top: pos?.top ?? 0, left: pos?.left ?? 0, minWidth: role === 'dialog' ? undefined : pos?.min, opacity: pos ? 1 : 0 };
  const body = <div ref={ref} role={role === 'presentation' ? undefined : role} aria-label={role === 'presentation' ? undefined : label} aria-modal={role === 'dialog' && asSheet ? true : undefined}
    className={`dp-panel font-sans antialiased${asSheet ? ' is-sheet' : ''} ${className}`} style={style} onKeyDown={onKeyDown}>
    {asSheet && head}
    {children}
  </div>;
  return createPortal(asSheet ? <><div className="dp-backdrop" aria-hidden="true" />{body}</> : body, document.body);
}

// The month grid. Arrows move a day or a week, Page Up / Page Down a month
// (with Shift, a year), Home / End the week's ends, Enter or Space picks.
// Days outside min/max stay reachable by keys but cannot be picked.
function Calendar({ value, min, max, onPick, autoFocus = false }) {
  const id = useId();
  const today = useMemo(startOfToday, []);
  const lo = toDay(min), hi = toDay(max);
  const allowed = d => (!lo || d >= lo) && (!hi || d <= hi);
  const first = toDay(value) || (allowed(today) ? today : lo && today < lo ? lo : hi || today);
  const [focus, setFocus] = useState(first);
  const [view, setView] = useState(monthStart(first));
  const grid = useRef(null);
  const moved = useRef(autoFocus);
  const picked = value ? value.slice(0, 10) : '';
  // the day that takes Tab: the focused one when it is in view, else the picked one, else the 1st
  const tabDay = sameMonth(focus, view) ? dayIso(focus) : picked && sameMonth(toDay(picked), view) ? picked : dayIso(view);
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    grid.current?.querySelector(`[data-day="${tabDay}"]`)?.focus();
  }, [tabDay]);
  useEffect(() => { if (autoFocus) grid.current?.querySelector(`[data-day="${tabDay}"]`)?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // a day picked from outside the grid (Today) comes into view
  const shown = useRef(picked);
  useEffect(() => {
    if (picked === shown.current) return;
    shown.current = picked;
    const d = toDay(picked);
    if (d) { setFocus(d); setView(monthStart(d)); }
  }, [picked]);
  const go = d => { moved.current = true; setFocus(d); if (!sameMonth(d, view)) setView(monthStart(d)); };
  // the month arrows keep the cursor, unless the arrow turns off (the min or
  // max month): then it goes to the grid, not to the page
  const page = n => {
    const next = monthStart(shiftMonth(view, n));
    if ((n < 0 && lo && new Date(next.getFullYear(), next.getMonth(), 0) < lo) || (n > 0 && hi && new Date(next.getFullYear(), next.getMonth() + 1, 1) > hi)) moved.current = true;
    setView(next); setFocus(f => shiftMonth(f, n));
  };
  const pick = d => { if (allowed(d)) onPick(dayIso(d)); };
  const onKeyDown = e => {
    const from = sameMonth(focus, view) ? focus : toDay(tabDay);
    const to = {
      ArrowLeft: () => shift(from, -1), ArrowRight: () => shift(from, 1), ArrowUp: () => shift(from, -7), ArrowDown: () => shift(from, 7),
      Home: () => shift(from, -from.getDay()), End: () => shift(from, 6 - from.getDay()),
      PageUp: () => shiftMonth(from, e.shiftKey ? -12 : -1), PageDown: () => shiftMonth(from, e.shiftKey ? 12 : 1),
    }[e.key];
    if (to) { e.preventDefault(); go(to()); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(from); }
  };
  const lead = view.getDay();
  const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i + 1))];
  while (cells.length % 7) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, r) => cells.slice(r * 7, r * 7 + 7));
  const prevOff = !!lo && new Date(view.getFullYear(), view.getMonth(), 0) < lo;
  const nextOff = !!hi && new Date(view.getFullYear(), view.getMonth() + 1, 1) > hi;
  const todayIso = dayIso(today);
  return <div className="dp-cal">
    <div className="dp-cal-head">
      <button type="button" className="dp-nav" aria-label="Previous month" disabled={prevOff} onClick={() => page(-1)}><ChevronLeft size={17} aria-hidden="true" /></button>
      <div id={`${id}-m`} className="dp-month" aria-live="polite">{view.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' })}</div>
      <button type="button" className="dp-nav" aria-label="Next month" disabled={nextOff} onClick={() => page(1)}><ChevronRight size={17} aria-hidden="true" /></button>
    </div>
    <table ref={grid} role="grid" className="dp-grid" aria-labelledby={`${id}-m`} onKeyDown={onKeyDown}>
      <thead><tr>{WEEK.map(w => <th key={w} scope="col" aria-label={w}>{w.slice(0, 2)}</th>)}</tr></thead>
      <tbody>{weeks.map((week, r) => <tr key={r}>{week.map((d, c) => {
        if (!d) return <td key={c} />;
        const iso = dayIso(d), ok = allowed(d), on = iso === picked, now = iso === todayIso;
        return <td key={c} aria-selected={on}>
          <button type="button" data-day={iso} tabIndex={iso === tabDay ? 0 : -1} aria-label={fullDay(d)} aria-current={now ? 'date' : undefined} aria-disabled={ok ? undefined : true}
            className={`dp-day${on ? ' is-on' : ''}${now ? ' is-today' : ''}`} onClick={() => { go(d); pick(d); }}>{d.getDate()}</button>
        </td>;
      })}</tr>)}</tbody>
    </table>
  </div>;
}

/* The typed time and its list. inline: the list always shows under the box
   (inside a date panel); otherwise it opens under the box while it has the
   cursor. Arrows walk the 15-minute steps; Enter or leaving the box keeps
   what was typed; a time it cannot read says so and keeps the old time. */
function TimeCombo({ value, onChange, label = 'Time', inline = false, disabled = false, invalid = false, describedBy, step = 15, className = '', allowEmpty = !inline }) {
  const id = useId();
  const input = useRef(null);
  const wrap = useRef(null);
  const list = useRef(null);
  const [text, setText] = useState(value ? timeLabel(value) : '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [bad, setBad] = useState(false);
  useEffect(() => { if (document.activeElement !== input.current) { setText(value ? timeLabel(value) : ''); setBad(false); } }, [value]);
  const options = useMemo(() => {
    const out = [];
    for (let t = 0; t < 1440; t += step) out.push(`${pad(Math.floor(t / 60))}:${pad(t % 60)}`);
    if (value && !out.includes(value)) { out.push(value); out.sort(); }
    return out;
  }, [step, value]);
  const near = hhmm => options.reduce((best, o, i) => (Math.abs(toMin(o) - toMin(hhmm)) < Math.abs(toMin(options[best]) - toMin(hhmm)) ? i : best), 0);
  const shown = inline || open;
  // the picked time (or the nearest step) is in the middle of the list when it shows
  const seen = useRef(false);
  useEffect(() => {
    if (!shown) { seen.current = false; return; }
    const i = active >= 0 ? active : near(value || '09:00');
    keepInView(list.current, list.current?.querySelector(`[data-i="${i}"]`), !seen.current);
    seen.current = true;
  }, [shown, active]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = t => { setBad(false); setText(timeLabel(t)); setOpen(false); setActive(-1); if (t !== value) onChange(t); };
  // an emptied box: no time (a field on its own), or back to the time it had (in a date panel)
  const commitText = () => {
    const s = text.trim();
    if (!s) { setBad(false); setOpen(false); if (allowEmpty) { setText(''); if (value) onChange(''); } else setText(value ? timeLabel(value) : ''); return; }
    const t = parseTime(s);
    if (t) commit(t); else setBad(true);
  };
  const move = d => {
    const from = active >= 0 ? active : near(parseTime(text) || value || '09:00');
    const i = Math.max(0, Math.min(options.length - 1, from + d));
    setActive(i); setText(timeLabel(options[i])); setBad(false); setOpen(true);
  };
  const onKeyDown = e => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!shown) { setOpen(true); setActive(near(parseTime(text) || value || '09:00')); } else move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'PageDown' || e.key === 'PageUp') { e.preventDefault(); move(e.key === 'PageDown' ? 4 : -4); }
    else if (e.key === 'Enter') { e.preventDefault(); commitText(); }
    else if (e.key === 'Escape' && open && !inline) { e.preventDefault(); e.stopPropagation(); setOpen(false); setActive(-1); setText(value ? timeLabel(value) : ''); setBad(false); }
  };
  const listId = `${id}-list`;
  const box = <input ref={input} type="text" className="dp-time-input" role="combobox" aria-expanded={shown} aria-controls={listId} aria-autocomplete="list"
    aria-activedescendant={shown && active >= 0 ? `${id}-o${active}` : undefined} aria-label={label} aria-invalid={invalid || bad || undefined}
    aria-describedby={[bad ? `${id}-bad` : null, describedBy].filter(Boolean).join(' ') || undefined}
    placeholder="Time" autoComplete="off" spellCheck={false} disabled={disabled} value={text}
    onChange={e => { const v = e.target.value; setText(v); setBad(false); setOpen(true); const t = parseTime(v); setActive(t ? near(t) : -1); }}
    onFocus={() => { if (!inline) { setOpen(true); setActive(-1); } }}
    onClick={() => { if (!inline && !disabled) setOpen(true); }}
    onBlur={() => { setOpen(false); setActive(-1); commitText(); }} onKeyDown={onKeyDown} />;
  const options$ = <ul ref={list} id={listId} role="listbox" aria-label={label} className="dp-times">
    {options.map((o, i) => <li key={o} id={`${id}-o${i}`} data-i={i} role="option" aria-selected={o === value} className={i === active ? 'is-active' : undefined}
      onMouseDown={e => e.preventDefault()} onClick={() => commit(o)}>{timeLabel(o)}{o === value && <Check size={14} strokeWidth={2.5} aria-hidden="true" />}</li>)}
  </ul>;
  const problem = bad && <p id={`${id}-bad`} className="dp-bad" role="alert">Type a time like 3:30 PM.</p>;
  // in a date panel the list fills what the calendar's height leaves (dp-times-fill)
  if (inline) return <div className={`dp-time ${className}`}><span className="dp-time-box"><Clock size={15} aria-hidden="true" />{box}</span>{problem}<div className="dp-times-fill">{options$}</div></div>;
  return <span className={`dp-time-field ${className}`}>
    <span ref={wrap} className={`dp-time-box dp-field${disabled ? ' is-off' : ''}`}><Clock size={15} aria-hidden="true" />{box}</span>
    {open && !disabled && <Panel anchor={wrap} role="presentation" className="dp-time-pop" onClose={() => setOpen(false)}>{options$}</Panel>}
    {problem}
  </span>;
}

// The time on its own (assessments keep the class day and time apart). HH:mm.
export function TimeField(props) { return <TimeCombo {...props} />; }

/* The panel's insides for a day, or a day and a time. mode 'date': a pick
   closes it. mode 'datetime': a pick keeps it open for the time; Done, a
   click outside or Escape closes it. Every change goes out as it happens, the
   way the native fields did. onClose(reason): 'pick' | 'done' | 'clear' |
   'escape' | 'outside'. returnFocus: where the cursor goes back to. */
export function DatePopover({ anchor, returnFocus = anchor, mode = 'date', label, value = '', min, max, defaultTime = '23:59', clearable = false, onChange, onClose }) {
  const phone = usePhone();
  const todayIso = dayIso(startOfToday());
  const minDay = min ? min.slice(0, 10) : undefined;
  const maxDay = max ? max.slice(0, 10) : undefined;
  const day = value ? value.slice(0, 10) : '';
  const time = mode === 'datetime' ? (value.slice(11, 16) || defaultTime) : '';
  const close = reason => { onClose(reason); if (reason !== 'outside') returnFocus.current?.focus(); };
  const pickDay = iso => {
    if (mode === 'date') { onChange(iso); close('pick'); return; }
    onChange(`${iso}T${time}`);
  };
  const todayOk = (!minDay || todayIso >= minDay) && (!maxDay || todayIso <= maxDay);
  // a time before min: on min's own day it has passed; otherwise say the earliest
  const early = mode === 'datetime' && min && min.length > 10 && value && value < min
    ? (minDay === todayIso ? 'That time has passed. Pick a later time.' : `Pick a time after ${valueLabel(min, 'datetime')}.`) : '';
  const done = <button type="button" className="dp-done" onClick={() => close('done')}>Done</button>;
  return <Panel anchor={anchor} label={label} sheet trap className={mode === 'datetime' ? 'is-datetime' : ''} onClose={close}
    head={<div className="dp-sheet-head"><span>{label}</span>{done}</div>}>
    <div className="dp-body">
      <Calendar value={value} min={minDay} max={maxDay} onPick={pickDay} autoFocus />
      {mode === 'datetime' && <TimeCombo inline label={`${label}, time`} value={time} onChange={t => onChange(`${day || (minDay && minDay > todayIso ? minDay : todayIso)}T${t}`)} />}
    </div>
    {early && <p className="dp-bad dp-early" role="alert">{early}</p>}
    {(todayOk || (clearable && value) || (mode === 'datetime' && !phone)) && <div className="dp-foot">
      {todayOk && <button type="button" className="dp-act" onClick={() => pickDay(todayIso)}>Today</button>}
      {clearable && value && <button type="button" className="dp-act" onClick={() => { onChange(''); close('clear'); }}>Clear</button>}
      {mode === 'datetime' && !phone && done}
    </div>}
  </Panel>;
}

// The form field that opens the panel. It shows the value as words
// ("Fri, Nov 6, 11:59 PM") or the placeholder in gray.
function PickerField({ mode, value = '', onChange, label, placeholder, min, max, defaultTime, clearable = false, disabled = false, invalid = false, describedBy, id, title, className = '', onClose }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const words = valueLabel(value, mode);
  return <>
    <button ref={btn} id={id} type="button" title={title} className={`dp-field${words ? '' : ' is-empty'} ${className}`} aria-haspopup="dialog" aria-expanded={open}
      aria-label={`${label}: ${words || placeholder}`} aria-invalid={invalid || undefined} aria-describedby={describedBy} disabled={disabled}
      onClick={() => setOpen(o => !o)} onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}>
      <CalendarDays size={16} aria-hidden="true" /><span className="dp-field-text">{words || placeholder}</span>
    </button>
    {open && <DatePopover anchor={btn} mode={mode} label={label} value={value} min={min} max={max} defaultTime={defaultTime} clearable={clearable}
      onChange={onChange} onClose={reason => { setOpen(false); onClose?.(reason); }} />}
  </>;
}

// YYYY-MM-DD.
export function DateField({ placeholder = 'Pick a date', ...props }) { return <PickerField mode="date" placeholder={placeholder} {...props} />; }
// YYYY-MM-DDTHH:mm. defaultTime: the time a first picked day gets.
export function DateTimeField({ placeholder = 'Pick a date and time', ...props }) { return <PickerField mode="datetime" placeholder={placeholder} {...props} />; }
